import { gql } from '@apollo/client'
import { graphql } from '@/graphql-setup'

export const PROFILE_QUERY = graphql(`
  query Profile($id: ID!, $withDocuments: Boolean = false) {
    user(id: $id) {
      id
      email
      fullName
      profilePicture
      phoneNumber
      address
      city
      postalCode
      birthNumber
      contactPersonName
      contactPersonPhoneNumber

      internalNumber
      startedAt

      activeDriverCard {
        id
      }

      driverCards {
        id
        cardIssuingMemberState
        driverIdentification
        issuedAt
        expiresAt
        isExpired
      }

      userTypes {
        id
        name
      }

      salaryGroups {
        id
        name
      }

      salaryWorkTimes {
        id
        priority
        dailyWorkLimit
        periodWorkLimit
        pauseDuration
        pauseThreshold
        paidPauseThreshold
        salaryDateStart
        salaryDateEnd
        name
        usePeriodOvertime
        useSalaryDayPayment
        salaryType
        useWeekOvertime

        useAutomaticDietGeneration
        useAutomaticSalaryDayGeneration

        defaultSalaryCode {
          id
          name
          externalId
        }

        dayOvertimeSalaryCode {
          id
          name
          externalId
        }

        weekOvertimeSalaryCode {
          id
          name
          externalId
        }

        periodAddons {
          id
          start
          end

          salaryCode {
            id
            name
            externalId
          }

          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
        defaultDepartment {
          id
          name
        }
        availableDepartments {
          id
          name
        }
        moduleConfig {
          id
          name
        }
      }

      leaderGroupsAsUser {
        id
        name
      }

      salaryGroups {
        id
        name
      }

      documents @include(if: $withDocuments) {
        edges {
          node {
            id
            name

            expirationDate
            notificationTime
            responsibleUser {
              id
              fullName
            }
            hasFiles
          }
        }
      }
    }
  }
`)

export const USER_SHALLOW_QUERY = graphql(`
  query UserShallow($id: ID!) {
    user(id: $id) {
      id
      fullName
    }
  }
`)

export const USER_FULL_NAME_QUERY = graphql(`
  query UserFullName($id: ID!) {
    user(id: $id) {
      id
      fullName
    }
  }
`)

export const USER_USER_TYPES_QUERY = graphql(`
  query UserUserTypes($id: ID!) {
    user(id: $id) {
      id
      userTypes {
        id
        name
        color
        isAdministrator
      }
    }
  }
`)

export const USER_SALARY_GROUPS_QUERY = graphql(`
  query UserSalaryGroups($id: ID!) {
    user(id: $id) {
      id
      salaryGroups {
        id
        name
      }
    }
  }
`)

export const USER_SALARY_WORK_TIME_QUERY = gql`
  query UserSalaryWorkTime($id: ID!, $moduleConfigName: String) {
    user(id: $id) {
      id
      salaryWorkTime(moduleConfigName: $moduleConfigName) {
        id
        priority
        dailyWorkLimit
        periodWorkLimit
        pauseDuration
        pauseThreshold
        paidPauseThreshold
        salaryDateStart
        salaryDateEnd
        name
        usePeriodOvertime

        moduleConfig {
          name
        }

        defaultSalaryCode {
          id
          name
          externalId
        }

        periodAddons {
          id
          start
          end

          salaryCode {
            id
            name
            externalId
          }

          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
      }
    }
  }
`

export const USERS_QUERY = gql`
  query Users(
    $q: String
    $first: Int
    $after: String
    $orderBy: String
    $userType: String
    $dropdown: String
    $includeInactive: Boolean
    $includeExpiredDocuments: Boolean
    $onlyIncludeUserDocumentCategories: [String]
  ) {
    allUsers(
      first: $first
      after: $after
      q: $q
      orderBy: $orderBy
      userType: $userType
      dropdown: $dropdown
      includeInactive: $includeInactive
      includeExpiredDocuments: $includeExpiredDocuments
      onlyIncludeUserDocumentCategories: $onlyIncludeUserDocumentCategories
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
          phoneNumber
          initials
          position
          profilePicture
          isActive
          hasRegistered
          internalNumber
          hasActiveDriverCard
          hasLoginCode
          driverFetchInfo {
            id
            lastFetch
            integrationEndpoint
          }
        }
      }
    }
  }
`

export const SIMPLE_USERS_QUERY = graphql(`
  query Users(
    $first: Int
    $after: String
    $q: String
    $orderBy: String
    $dropdown: String
    $filterSalaryResponsible: Boolean
    $filterLeaderGroup: Boolean
    $includeInactive: Boolean
  ) {
    allUsers(
      first: $first
      after: $after
      q: $q
      orderBy: $orderBy
      dropdown: $dropdown
      filterSalaryResponsible: $filterSalaryResponsible
      filterLeaderGroup: $filterLeaderGroup
      includeInactive: $includeInactive
    ) {
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const ALL_USERS_SHALLOW_QUERY = gql`
  query UsersShallow(
    $q: String
    $first: Int
    $after: String
    $orderBy: String
    $dropdown: String
    $filterSalaryResponsible: Boolean
    $filterLeaderGroup: Boolean
  ) {
    allUsers(
      q: $q
      first: $first
      after: $after
      orderBy: $orderBy
      dropdown: $dropdown
      filterSalaryResponsible: $filterSalaryResponsible
      filterLeaderGroup: $filterLeaderGroup
    ) {
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const USER_TYPES_QUERY = gql`
  query UserTypes($permission: String, $dropdown: String) {
    allUserTypes(permission: $permission, dropdown: $dropdown) {
      edges {
        node {
          id
          name
          users {
            edges {
              node {
                id
                fullName
                username
              }
            }
          }
        }
      }
    }
  }
`

export const USERS_CHILDCARE_QUERY = graphql(`
  query UsersChildcare {
    allUsersList(includeSuperuser: true, orderBy: "lastName") {
      __typename
      id
      firstName
      lastName
      fullName
      initials
      profilePicture

      childcare {
        __typename
        numberOfChildren
        singleParent
        childChronicIllness
      }
    }
  }
`)

export const USER_TYPES_SHALLOW_QUERY = gql`
  query UserTypesShallow {
    allUserTypes {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

export const USER_TYPES_WITH_PERMISSIONS_QUERY = gql`
  query UserTypesWithPermissions {
    allUserTypes {
      edges {
        node {
          id
          name
          editable
          isAdministrator
          permissions
        }
      }
    }
  }
`

export const USER_TYPES_WITH_DROPDOWNS_QUERY = gql`
  query UserTypesWithDropdowns {
    allUserTypes {
      edges {
        node {
          id
          name
          dropdowns
        }
      }
    }
  }
`

export const DRIVER_CARD_QUERY = gql`
  query DriverCard($id: ID!) {
    driverCard(id: $id) {
      id
      cardIssuingMemberState
      driverIdentification
      issuedAt
      expiresAt
      isExpired
      user {
        id
        fullName
      }
    }
  }
`

export const USER_DOCUMENT_FILES_FOR_DOCUMENT_QUERY = gql`
  query UserDocumentFilesForDocument($userDocument: ID) {
    allUserDocumentFiles(userDocument: $userDocument) {
      edges {
        node {
          id
          file
          name
          originalName
          createdAt
        }
      }
    }
  }
`

export const ALL_USER_DOCUMENTS_QUERY = gql`
  query AllUserDocuments(
    $q: String
    $user: ID
    $responsibleUser: ID
    $orderBy: String
    $first: Int
    $after: String
    $categories: [ID]
  ) {
    allUserDocuments(
      q: $q
      user: $user
      responsibleUser: $responsibleUser
      orderBy: $orderBy
      first: $first
      after: $after
      categories: $categories
    ) {
      edges {
        node {
          id
          name

          expirationDate
          notificationTime

          hasFiles

          user {
            id
            fullName
          }

          responsibleUser {
            id
            fullName
            firstName
            lastName
          }
          categories {
            id
            name
          }
        }
      }
    }
  }
`

export const NEEDS_PASSWORD_CHANGE_QUERY = gql`
  query NeedsPasswordChange {
    me {
      id
      needsPasswordChange
    }
  }
`

export const ALL_USER_DOCUMENT_CATEGORIES_QUERY = gql`
  query AllUserDocumentCategories($includeExpiredDocuments: Boolean = false) {
    allUserDocumentCategories {
      edges {
        node {
          id
          name
          editable
          numUsers(includeExpiredDocuments: $includeExpiredDocuments)
        }
      }
    }
  }
`

export const ALL_USER_EQUIPMENT_TYPES_QUERY = gql`
  query AllUserEquipmentTypes {
    allUserEquipmentTypes {
      edges {
        node {
          id
          name
          description
          editable
        }
      }
    }
  }
`

export const ALL_USER_EQUIPMENT_QUERY = graphql(`
  query AllUserEquipment($user: ID, $equipmentType: ID, $orderBy: String) {
    allUserEquipment(
      user: $user
      equipmentType: $equipmentType
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          name
          assignedDate
          returnDate
          user {
            id
            fullName
          }
          equipmentType {
            id
            name
          }
        }
      }
    }
  }
`)
